import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import LanguageIcon from "@mui/icons-material/Language";
import HomeIcon from "@mui/icons-material/Home";
import PropTypes from "prop-types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./WebsiteBuilderPage.module.scss";
import { fetchHostPropertySelectOptions } from "../services/hostTaskPropertyService";
import PulseBarsLoader from "../../../components/loaders/PulseBarsLoader";
import arrowLeftIcon from "../../../images/arrow-left-icon.svg";
import arrowRightIcon from "../../../images/arrow-right-icon.svg";
import TemplateSilhouette from "./TemplateSilhouette";
import {
  DEFAULT_WEBSITE_TEMPLATE_ID,
  WEBSITE_TEMPLATE_OPTIONS,
  getWebsiteTemplateById,
  getWebsiteTemplateCopy,
  isWebsiteTemplateBuilderEnabled,
} from "./websiteTemplates";
import WebsiteTemplatePreview from "./rendering/WebsiteTemplatePreview";
import { isWebsiteTemplateImplemented } from "./rendering/templateRegistry";
import {
  PREVIEW_BUILD_STEPS,
  PREVIEW_STAGE,
  runWebsitePreviewBuildWorkflow,
} from "./services/websitePreviewWorkflow";
import {
  recordWebsiteHostAnalyticsEventSafely,
  recordWebsiteHostAnalyticsEventWithRetry,
} from "./analytics/websiteAnalyticsService";
import {
  createWebsiteBuildAttempt,
  createWebsiteBuildFlowId,
  getBuildAttemptDurationMs,
  waitForNextPaint,
  WEBSITE_BUILD_FAILURE_PHASE_PERSIST,
} from "./analytics/websiteBuildAnalytics";
import {
  WEBSITE_BUILD_FAILED_EVENT,
  WEBSITE_BUILD_FLOW_ABANDONED_EVENT,
  WEBSITE_BUILD_FLOW_STARTED_EVENT,
  WEBSITE_BUILD_STARTED_EVENT,
  WEBSITE_BUILD_SUCCEEDED_EVENT,
  WEBSITE_PREVIEW_READY_EVENT,
} from "./analytics/websiteAnalyticsEventTypes";
import {
  deleteWebsiteDraft,
  fetchWebsiteDrafts,
  upsertWebsiteDraft,
} from "./services/websiteDraftService";
import { fetchWebsiteSiteByPropertyId } from "./services/websiteSiteService";
import { formatPublishedAtLabel } from "./services/websiteListingChange";
import { buildWebsiteDraftPreviewCacheKeyMap, resolveWebsiteDraftLiveSiteState } from "./services/websiteLiveSiteState";
import WebsiteDomainPanel from "./domains/WebsiteDomainPanel";
import { fetchWebsitePropertyDetails } from "./services/websitePropertyService";
import { buildWebsiteTemplateModel } from "./rendering/buildWebsiteTemplateModel";
import {
  DEFAULT_WEBSITE_CONTACT_ACCENT_COLOR,
  DEFAULT_WEBSITE_CONTACT_BACKGROUND_COLOR,
  DEFAULT_WEBSITE_CONTACT_DESCRIPTION,
  DEFAULT_WEBSITE_CONTACT_SECTION_TITLE,
  DEFAULT_WEBSITE_CONTACT_TITLE,
  WEBSITE_CONTACT_AVATAR_MODE_HOST,
} from "./config/websiteContactSectionConfig";
import { applyWebsiteDraftContentOverrides } from "./rendering/websiteDraftContentOverrides";
import { applyWebsiteDraftThemeOverrides } from "./rendering/websiteDraftThemeOverrides";
import { placeholderImage, resolveAccommodationImageUrl } from "../../../utils/accommodationImage";
import { WEBSITE_DRAFT_DELETE_REASONS } from "./websiteDeleteReasons";
import { buildPublishedWebsiteHref } from "./websitePublicSiteLinks";

const EMPTY_SELECTION = "";
const PHOTO_CARD_VARIANT_CLASSES = [styles.photoCard1, styles.photoCard2, styles.photoCard3];
const PREVIEW_BUILD_STEP_KEYS = PREVIEW_BUILD_STEPS.map((step) => step.key);

const PROPERTY_STATUS_LABELS = {
  ACTIVE: "Live",
  INACTIVE: "Draft",
  ARCHIVED: "Archived",
  PENDING_REVIEW: "Pending review",
  REJECTED: "Rejected",
};

const SUMMARY_DESCRIPTION_WORD_LIMIT = 23;
const WORKSPACE_TAB_BUILDER = "builder";
const WORKSPACE_TAB_WEBSITES = "websites";
const DELETE_WEBSITE_DRAFT_STEP_REASON = "reason";
const DELETE_WEBSITE_DRAFT_STEP_CONFIRM = "confirm";

const getPropertyStatusLabel = (status) =>
  PROPERTY_STATUS_LABELS[String(status || "").toUpperCase()] || "Unknown";

const truncateDescription = (description, wordLimit = SUMMARY_DESCRIPTION_WORD_LIMIT) => {
  const words = String(description || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length <= wordLimit) {
    return words.join(" ");
  }

  return `${words.slice(0, wordLimit).join(" ")}...`;
};

const getImportedPhotoSummary = (importedImageCount, emptyLabel = "No photos imported yet") => {
  if (importedImageCount < 1) {
    return emptyLabel;
  }

  return `${importedImageCount} photo${importedImageCount === 1 ? "" : "s"} imported`;
};

const getPropertySelectPlaceholder = ({ isLoading, isLoadingWebsiteDrafts }) => {
  if (isLoading) {
    return "Loading your listings...";
  }

  if (isLoadingWebsiteDrafts) {
    return "Checking website availability...";
  }

  return "Choose a listing";
};

const getGalleryAnimationClassName = (direction) => {
  if (direction === "forward") {
    return styles.galleryAnimatedImageForward;
  }

  if (direction === "backward") {
    return styles.galleryAnimatedImageBackward;
  }

  return "";
};

const getGalleryViewAlt = (propertyLabel, activeIndex) => `${propertyLabel} view ${activeIndex + 1}`;

const getPhotoCardClassName = (photoIndex) => {
  const variantClassName = PHOTO_CARD_VARIANT_CLASSES[photoIndex] || "";
  return `${styles.photoCard} ${variantClassName}`.trim();
};

const getDraftContentOverrides = (draft) =>
  draft?.contentOverrides && typeof draft.contentOverrides === "object" ? draft.contentOverrides : {};

const getDraftPublishedContentOverrides = (draft) =>
  draft?.publishedContentOverrides && typeof draft.publishedContentOverrides === "object"
    ? draft.publishedContentOverrides
    : {};

const getDraftPublishedThemeOverrides = (draft) =>
  draft?.publishedThemeOverrides && typeof draft.publishedThemeOverrides === "object"
    ? draft.publishedThemeOverrides
    : {};

const getDraftDisplayTitle = (draft, contentOverrides = getDraftContentOverrides(draft)) => {
  return (
    String(contentOverrides.siteTitle || "").trim() ||
    String(draft?.propertyTitle || "").trim() ||
    "Untitled listing website"
  );
};

const buildWebsitePreviewPath = (draftId) => `/website-preview/${encodeURIComponent(draftId)}`;
const getDraftPropertyId = (draft) => String(draft?.propertyId || "").trim();
const getWebsiteDraftPreviewCacheKey = (draft) =>
  `${String(draft?.updatedAt || "").trim()}::${String(draft?.templateKey || "").trim()}`;

const keepActivePropertyEntries = (entriesByPropertyId, activePropertyIds) => {
  let hasRemovedEntry = false;
  const nextEntries = {};

  Object.entries(entriesByPropertyId || {}).forEach(([propertyId, entry]) => {
    if (activePropertyIds.has(propertyId)) {
      nextEntries[propertyId] = entry;
      return;
    }

    hasRemovedEntry = true;
  });

  return hasRemovedEntry ? nextEntries : entriesByPropertyId;
};

const pruneWebsiteDraftPreviewCacheKeys = (previewCacheKeys, activePropertyIds) => {
  const nextPreviewCacheKeys = {};

  Object.entries(previewCacheKeys || {}).forEach(([propertyId, previewCacheKey]) => {
    if (activePropertyIds.has(propertyId)) {
      nextPreviewCacheKeys[propertyId] = previewCacheKey;
    }
  });

  return nextPreviewCacheKeys;
};

const buildWebsiteDraftPreviewModelMap = (previewEntries) =>
  Object.fromEntries(previewEntries.map(([propertyId, previewModel]) => [propertyId, previewModel]));

const buildWebsiteDraftLiveSiteStateMap = (previewEntries) =>
  Object.fromEntries(previewEntries.map(([propertyId, , , liveSiteState]) => [propertyId, liveSiteState]));

const buildImageVariantMap = (images) => {
  const imageVariantMap = new Map();
  (Array.isArray(images) ? images : []).forEach((image) => {
    const thumbUrl = resolveAccommodationImageUrl(image, "thumb");
    const webUrl = resolveAccommodationImageUrl(image, "web");

    if (thumbUrl && thumbUrl !== placeholderImage) {
      imageVariantMap.set(thumbUrl, thumbUrl);
      if (webUrl && webUrl !== placeholderImage) {
        imageVariantMap.set(webUrl, thumbUrl);
      }
    }
  });
  return imageVariantMap;
};

const mapImageOverridesToThumbnails = (contentOverrides, imageVariantMap) => {
  if (!contentOverrides || typeof contentOverrides !== "object" || imageVariantMap.size < 1) {
    return contentOverrides || {};
  }

  const nextOverrides = { ...contentOverrides };
  if (nextOverrides.heroImage) {
    nextOverrides.heroImage = imageVariantMap.get(nextOverrides.heroImage) || nextOverrides.heroImage;
  }

  if (nextOverrides.residenceImage) {
    nextOverrides.residenceImage =
      imageVariantMap.get(nextOverrides.residenceImage) || nextOverrides.residenceImage;
  }

  if (Array.isArray(nextOverrides.galleryImages)) {
    nextOverrides.galleryImages = nextOverrides.galleryImages.map(
      (imageUrl) => imageVariantMap.get(imageUrl) || imageUrl
    );
  }

  return nextOverrides;
};

const formatDraftUpdatedAt = (updatedAt) => {
  const parsedValue = Number(updatedAt);
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return "Unknown update time";
  }

  try {
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(parsedValue));
  } catch {
    return "Unknown update time";
  }
};

const buildDraftCardFallbackPreviewModel = (draft) => {
  const contentOverrides = getDraftPublishedContentOverrides(draft);
  const themeOverrides = getDraftPublishedThemeOverrides(draft);
  const locationLabel = String(draft?.location || "").trim();
  const title = getDraftDisplayTitle(draft, contentOverrides);
  const subtitle = String(draft?.propertySubtitle || "").trim();
  const selectedGalleryImages = Array.isArray(contentOverrides.galleryImages)
    ? contentOverrides.galleryImages.map((imageUrl) => String(imageUrl || "").trim()).filter(Boolean)
    : [];
  const heroImage = String(contentOverrides.heroImage || selectedGalleryImages[0] || placeholderImage).trim();
  const residenceImage = String(
    contentOverrides.residenceImage || selectedGalleryImages[0] || heroImage
  ).trim();
  const galleryImages = [heroImage, ...selectedGalleryImages].filter(Boolean).slice(0, 5);
  const normalizedGalleryImages = galleryImages.length > 0 ? galleryImages : [placeholderImage];
  const previewImages = normalizedGalleryImages.slice(0, 3);

  const themedModel = applyWebsiteDraftThemeOverrides(
    {
      source: {
        propertyId: String(draft?.propertyId || "").trim(),
        hostId: String(draft?.hostId || "").trim(),
        status: String(draft?.propertyStatus || draft?.status || "DRAFT").trim(),
        locale: "en",
      },
      host: {
        name: String(draft?.hostName || draft?.hostGivenName || "Host").trim() || "Host",
        profileImage: "",
        initial:
          String(draft?.hostName || draft?.hostGivenName || "H").trim().charAt(0).toUpperCase() || "H",
      },
      site: {
        title,
        subtitle,
        templateReadyTitle: title,
        locationLabel,
      },
      media: {
        heroImage,
        residenceImage,
        galleryImages: normalizedGalleryImages,
        previewImages,
        featuredGalleryImages: normalizedGalleryImages,
      },
      hero: {
        eyebrow: locationLabel || "Saved website draft",
        title,
        subtitle,
        description: subtitle || "Saved website preview for this selected listing.",
        imageUrl: heroImage,
      },
      stay: {
        propertyTypeLabel: "",
        guests: 0,
        bedrooms: 0,
        beds: 0,
        bathrooms: 0,
        guestsLabel: "",
        bedroomsLabel: "",
        bedsLabel: "",
        bathroomsLabel: "",
        nightlyRate: 0,
        nightlyRateLabel: "",
        minimumStay: 0,
        minimumStayLabel: "",
        checkInLabel: "",
        checkOutLabel: "",
        stats: [],
      },
      location: {
        city: "",
        country: "",
        label: locationLabel,
        narrative: locationLabel ? `This stay is located in ${locationLabel}.` : "",
      },
      amenities: {
        featured: [],
        all: [],
        summary: "",
      },
      policies: {
        featured: [],
        all: [],
        summary: "",
      },
      availability: {
        externalBlockedDates: [],
        unavailableDateKeys: [],
        blockedDateCount: 0,
        externalBlockedDateCount: 0,
        unavailableDateCount: 0,
        hasExternalCalendarSync: false,
        syncedSourceCount: 0,
        syncSummary: "Loading imported calendar context",
        externalBlockedSummary: "Loading imported external bookings",
        unavailableDateSummary: "Loading PMS blocked dates",
        blockedDateSummary: "Loading imported blocked dates",
        lastSyncLabel: "",
        nextBlockedDate: "",
        nextBlockedLabel: "",
        callout: "Saved draft preview uses persisted content first and hydrates listing details in the background.",
      },
      gallery: {
        images: normalizedGalleryImages,
        countLabel: `${normalizedGalleryImages.length} imported photo${
          normalizedGalleryImages.length === 1 ? "" : "s"
        }`,
      },
      trustCards: [
        {
          id: "draft-summary",
          iconAmenityId: "7",
          title: "Draft summary",
          description: subtitle || "Saved website draft ready to continue editing.",
        },
        {
          id: "draft-location",
          iconAmenityId: "57",
          title: "Location context",
          description: locationLabel || "Location details are attached to this saved website draft.",
        },
        {
          id: "draft-template",
          iconAmenityId: "55",
          title: "Template state",
          description: String(draft?.templateKey || "Template selected").trim(),
        },
      ],
      journeyStops: [
        {
          id: "draft-step-1",
          title: "Draft created",
          description: "This website can be reopened from the workspace at any time.",
        },
        {
          id: "draft-step-2",
          title: "Content editable",
          description: "Text, visibility, and image slots can be adjusted in the editor.",
        },
        {
          id: "draft-step-3",
          title: "Ready for next phase",
          description: "Publish and domain connection can build on top of this draft state.",
        },
      ],
      callToAction: {
        label: "Open editor",
        note: "Saved website draft ready for continued editing.",
      },
      contactSection: {
        title: DEFAULT_WEBSITE_CONTACT_SECTION_TITLE,
        caption: DEFAULT_WEBSITE_CONTACT_TITLE,
        description: DEFAULT_WEBSITE_CONTACT_DESCRIPTION,
        accentColor: DEFAULT_WEBSITE_CONTACT_ACCENT_COLOR,
        backgroundColor: DEFAULT_WEBSITE_CONTACT_BACKGROUND_COLOR,
        avatarMode: WEBSITE_CONTACT_AVATAR_MODE_HOST,
        avatarImage: "",
      },
      visibility: {
        topBar: true,
        trustCards: true,
        gallerySection: true,
        amenitiesPanel: true,
        availabilityCalendar: true,
        callToAction: true,
        journeyStops: true,
        contactSection: true,
        chatWidget: true,
      },
    },
    themeOverrides
  );

  return applyWebsiteDraftContentOverrides(themedModel, contentOverrides, draft.templateKey);
};

const loadWebsiteDraftListingDetails = async (draft) => {
  try {
    return await fetchWebsitePropertyDetails(draft.propertyId);
  } catch {
    return null;
  }
};

const buildWebsiteDraftPreviewModel = (draft, propertyDetails) => {
  if (!propertyDetails) {
    return buildDraftCardFallbackPreviewModel(draft);
  }

  try {
    const baseModel = buildWebsiteTemplateModel({
      propertyDetails,
      summaryProperty: null,
      imageVariant: "thumb",
    });
    const thumbContentOverrides = mapImageOverridesToThumbnails(
      getDraftPublishedContentOverrides(draft),
      buildImageVariantMap(propertyDetails?.images)
    );
    const themedModel = applyWebsiteDraftThemeOverrides(
      baseModel,
      getDraftPublishedThemeOverrides(draft)
    );
    return applyWebsiteDraftContentOverrides(themedModel, thumbContentOverrides, draft.templateKey);
  } catch {
    return buildDraftCardFallbackPreviewModel(draft);
  }
};

const buildWebsiteDraftPreviewEntry = async (draft) => {
  const propertyId = getDraftPropertyId(draft);
  const propertyDetails = await loadWebsiteDraftListingDetails(draft);
  const previewModel = buildWebsiteDraftPreviewModel(draft, propertyDetails);
  const liveSiteState = await resolveWebsiteDraftLiveSiteState(draft, propertyDetails);
  const previewCacheKey = getWebsiteDraftPreviewCacheKey(draft);
  return [propertyId, previewModel, previewCacheKey, liveSiteState];
};

function WebsiteDraftDeleteDialog({
  draft = null,
  deleteReasons,
  deleteStep,
  isDeleting,
  onClose,
  onBackToReasons,
  onConfirm,
  onReasonToggle,
  onReasonNext,
}) {
  if (!draft) {
    return null;
  }

  const isReasonStep = deleteStep === DELETE_WEBSITE_DRAFT_STEP_REASON;
  const hasSelectedReason = deleteReasons.length > 0;

  return (
    <dialog
      open
      className={styles.deleteDraftOverlay}
      aria-labelledby="delete-website-draft-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section className={styles.deleteDraftDialog}>
        <p className={styles.deleteDraftEyebrow}>Delete website</p>
        {isReasonStep ? (
          <>
            <h3 id="delete-website-draft-title" className={styles.deleteDraftTitle}>
              Why are you deleting this website?
            </h3>
            <p className={styles.deleteDraftCopy}>Choose all reasons that apply.</p>

            <fieldset className={styles.deleteReasonFieldset}>
              <legend className={styles.deleteReasonLegend}>Select at least one reason to continue.</legend>
              <div className={styles.deleteReasonList}>
                {WEBSITE_DRAFT_DELETE_REASONS.map((reason) => (
                  <label key={reason} className={styles.deleteReasonOption}>
                    <input
                      type="checkbox"
                      value={reason}
                      checked={deleteReasons.includes(reason)}
                      onChange={() => onReasonToggle(reason)}
                      disabled={isDeleting}
                    />
                    <span>{reason}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </>
        ) : (
          <>
            <h3 id="delete-website-draft-title" className={styles.deleteDraftTitle}>
              Delete this website permanently?
            </h3>
            <p className={styles.deleteDraftCopy}>
              This removes the saved website draft for <strong>{getDraftDisplayTitle(draft)}</strong>.
            </p>
            <p className={styles.deleteDraftCopy}>
              The listing will become available again in the Build website dropdown.
            </p>
            <p className={styles.deleteDraftCopy}>
              This <strong>does not</strong> delete the listing itself.
            </p>
          </>
        )}

        <div className={styles.deleteDraftActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={isReasonStep ? onClose : onBackToReasons}
            disabled={isDeleting}
          >
            {isReasonStep ? "Cancel" : "Back"}
          </button>
          {isReasonStep ? (
            <button
              type="button"
              className={`${styles.primaryButton} ${styles.dangerButton}`.trim()}
              onClick={onReasonNext}
              disabled={isDeleting || !hasSelectedReason}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.primaryButton} ${styles.dangerButton}`.trim()}
              onClick={onConfirm}
              disabled={isDeleting || !hasSelectedReason}
            >
              {isDeleting ? "Deleting..." : "Delete permanently"}
            </button>
          )}
        </div>
      </section>
    </dialog>
  );
}

WebsiteDraftDeleteDialog.propTypes = {
  draft: PropTypes.shape({
    contentOverrides: PropTypes.shape({
      siteTitle: PropTypes.string,
    }),
    propertyTitle: PropTypes.string,
  }),
  deleteReasons: PropTypes.arrayOf(PropTypes.string).isRequired,
  deleteStep: PropTypes.oneOf([
    DELETE_WEBSITE_DRAFT_STEP_REASON,
    DELETE_WEBSITE_DRAFT_STEP_CONFIRM,
  ]).isRequired,
  isDeleting: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBackToReasons: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onReasonToggle: PropTypes.func.isRequired,
  onReasonNext: PropTypes.func.isRequired,
};

function WebsiteBuilderPage() {
  const { language } = useContext(LanguageContext);
  const templateCopy = getWebsiteTemplateCopy(language);
  const [workspaceTab, setWorkspaceTab] = useState(WORKSPACE_TAB_WEBSITES);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(EMPTY_SELECTION);
  const [selectedTemplateId, setSelectedTemplateId] = useState(DEFAULT_WEBSITE_TEMPLATE_ID);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [galleryAnimationDirection, setGalleryAnimationDirection] = useState("idle");
  const [galleryAnimationTick, setGalleryAnimationTick] = useState(0);
  const [previewStage, setPreviewStage] = useState(PREVIEW_STAGE.idle);
  const [previewBuildPhase, setPreviewBuildPhase] = useState(PREVIEW_BUILD_STEPS[0].key);
  const [previewModel, setPreviewModel] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [websiteDrafts, setWebsiteDrafts] = useState([]);
  const [isLoadingWebsiteDrafts, setIsLoadingWebsiteDrafts] = useState(true);
  const [websiteDraftsError, setWebsiteDraftsError] = useState("");
  const [websiteDraftPreviewModels, setWebsiteDraftPreviewModels] = useState({});
  const [websiteDraftLiveSiteStates, setWebsiteDraftLiveSiteStates] = useState({});
  const [isPersistingWebsiteDraft, setIsPersistingWebsiteDraft] = useState(false);
  const [persistWebsiteDraftError, setPersistWebsiteDraftError] = useState("");
  const [websiteDraftPendingDelete, setWebsiteDraftPendingDelete] = useState(null);
  const [websiteDraftDeleteReasons, setWebsiteDraftDeleteReasons] = useState([]);
  const [websiteDraftDeleteStep, setWebsiteDraftDeleteStep] = useState(DELETE_WEBSITE_DRAFT_STEP_REASON);
  const [isDeletingWebsiteDraft, setIsDeletingWebsiteDraft] = useState(false);
  const previewSectionRef = useRef(null);
  const websiteBuildFlowRef = useRef(null);
  const websiteBuildAttemptRef = useRef(null);
  const websiteDraftPreviewCacheKeysRef = useRef({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPropertyIdParam = String(searchParams.get("propertyId") || "").trim();
  const hasAppliedInitialPropertyIdRef = useRef(false);

  const draftedPropertyIds = useMemo(
    () =>
      new Set(
        websiteDrafts
          .map((draft) => String(draft?.propertyId || "").trim())
          .filter(Boolean)
      ),
    [websiteDrafts]
  );

  const availablePropertyOptions = useMemo(
    () =>
      propertyOptions.filter((propertyOption) => {
        const propertyId = String(propertyOption?.value || "").trim();
        return propertyId && !draftedPropertyIds.has(propertyId);
      }),
    [draftedPropertyIds, propertyOptions]
  );

  const loadProperties = async () => {
    setIsLoading(true);
    setLoadError("");

    try {
      const nextPropertyOptions = await fetchHostPropertySelectOptions();
      setPropertyOptions(nextPropertyOptions);
      setSelectedPropertyId((currentPropertyId) =>
        nextPropertyOptions.some((option) => option.value === currentPropertyId)
          ? currentPropertyId
          : EMPTY_SELECTION
      );
    } catch (error) {
      setPropertyOptions([]);
      setSelectedPropertyId(EMPTY_SELECTION);
      setLoadError(error?.message || "We could not load your listings.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadHostWebsiteDrafts = async () => {
    setIsLoadingWebsiteDrafts(true);
    setWebsiteDraftsError("");

    try {
      const drafts = await fetchWebsiteDrafts();
      setWebsiteDrafts(drafts);
    } catch (error) {
      setWebsiteDrafts([]);
      setWebsiteDraftsError(error?.message || "We could not load your saved website drafts.");
    } finally {
      setIsLoadingWebsiteDrafts(false);
    }
  };

  useEffect(() => {
    void loadProperties();
    void loadHostWebsiteDrafts();
  }, []);

  // Pre-select the listing passed via ?propertyId= (e.g. from the Onboarding
  // hub) once both the property list and the draft list have loaded, so we
  // know whether it already has a draft. Applies once per page load only —
  // deliberately does not re-run after the host builds a draft, which would
  // otherwise yank them back to a tab they may have already left.
  useEffect(() => {
    if (hasAppliedInitialPropertyIdRef.current) return;
    if (!initialPropertyIdParam) return;
    if (isLoading || isLoadingWebsiteDrafts) return;

    hasAppliedInitialPropertyIdRef.current = true;

    const matchesKnownProperty = propertyOptions.some(
      (option) => option.value === initialPropertyIdParam
    );
    if (!matchesKnownProperty) return;

    if (draftedPropertyIds.has(initialPropertyIdParam)) {
      // Already has a draft - the default "My websites" tab already shows it.
      setWorkspaceTab(WORKSPACE_TAB_WEBSITES);
    } else {
      setWorkspaceTab(WORKSPACE_TAB_BUILDER);
      setSelectedPropertyId(initialPropertyIdParam);
    }
  }, [initialPropertyIdParam, isLoading, isLoadingWebsiteDrafts, propertyOptions, draftedPropertyIds]);

  useEffect(() => {
    setSelectedPropertyId((currentPropertyId) => {
      if (!currentPropertyId) {
        return currentPropertyId;
      }

      if (previewStage !== PREVIEW_STAGE.idle || isPersistingWebsiteDraft) {
        return currentPropertyId;
      }

      const listingStillAvailable = availablePropertyOptions.some(
        (propertyOption) => propertyOption.value === currentPropertyId
      );

      return listingStillAvailable ? currentPropertyId : EMPTY_SELECTION;
    });
  }, [availablePropertyOptions, isPersistingWebsiteDraft, previewStage]);

  useEffect(() => {
    let isMounted = true;

    const loadWebsiteDraftPreviewModels = async () => {
      if (websiteDrafts.length < 1) {
        if (isMounted) {
          websiteDraftPreviewCacheKeysRef.current = {};
          setWebsiteDraftPreviewModels({});
          setWebsiteDraftLiveSiteStates({});
        }
        return;
      }

      if (workspaceTab !== WORKSPACE_TAB_WEBSITES) {
        return;
      }

      const activePropertyIds = new Set(websiteDrafts.map(getDraftPropertyId).filter(Boolean));
      websiteDraftPreviewCacheKeysRef.current = pruneWebsiteDraftPreviewCacheKeys(
        websiteDraftPreviewCacheKeysRef.current,
        activePropertyIds
      );
      if (isMounted) {
        setWebsiteDraftPreviewModels((currentPreviewModels) =>
          keepActivePropertyEntries(currentPreviewModels, activePropertyIds)
        );
        setWebsiteDraftLiveSiteStates((currentLiveSiteStates) =>
          keepActivePropertyEntries(currentLiveSiteStates, activePropertyIds)
        );
      }

      const draftsNeedingPreviewModels = websiteDrafts.filter((draft) => {
        const propertyId = getDraftPropertyId(draft);
        if (!propertyId) {
          return false;
        }

        return websiteDraftPreviewCacheKeysRef.current[propertyId] !== getWebsiteDraftPreviewCacheKey(draft);
      });

      if (draftsNeedingPreviewModels.length < 1) {
        return;
      }

      const previewEntries = await Promise.all(draftsNeedingPreviewModels.map(buildWebsiteDraftPreviewEntry));

      if (!isMounted) {
        return;
      }

      websiteDraftPreviewCacheKeysRef.current = {
        ...websiteDraftPreviewCacheKeysRef.current,
        ...buildWebsiteDraftPreviewCacheKeyMap(previewEntries),
      };
      setWebsiteDraftPreviewModels((currentPreviewModels) => ({
        ...currentPreviewModels,
        ...buildWebsiteDraftPreviewModelMap(previewEntries),
      }));
      setWebsiteDraftLiveSiteStates((currentLiveSiteStates) => ({
        ...currentLiveSiteStates,
        ...buildWebsiteDraftLiveSiteStateMap(previewEntries),
      }));
    };

    void loadWebsiteDraftPreviewModels();

    return () => {
      isMounted = false;
    };
  }, [websiteDrafts, workspaceTab]);

  const selectedProperty =
    propertyOptions.find((propertyOption) => propertyOption.value === selectedPropertyId) || null;
  const selectedPropertyIsAvailable = availablePropertyOptions.some(
    (propertyOption) => propertyOption.value === selectedPropertyId
  );
  const shouldKeepSelectedPropertyVisible =
    Boolean(selectedProperty) &&
    Boolean(selectedPropertyId) &&
    !selectedPropertyIsAvailable &&
    (previewStage !== PREVIEW_STAGE.idle || isPersistingWebsiteDraft);
  const propertySelectOptions = shouldKeepSelectedPropertyVisible
    ? [selectedProperty, ...availablePropertyOptions]
    : availablePropertyOptions;
  const previewImages = selectedProperty?.previewImages || [];
  const galleryImages = selectedProperty?.galleryImages || previewImages;
  const importedImageCount = selectedProperty?.imageCount || 0;
  const summaryDescription = truncateDescription(selectedProperty?.description);
  const selectedTemplate = getWebsiteTemplateById(selectedTemplateId, language);
  const selectedTemplateIsImplemented = isWebsiteTemplateImplemented(selectedTemplateId);
  const selectedTemplateIsBuilderEnabled = isWebsiteTemplateBuilderEnabled(selectedTemplateId);
  const selectedTemplateIsBuildable = selectedTemplateIsImplemented && selectedTemplateIsBuilderEnabled;
  const activeGalleryImage = galleryImages[activeGalleryIndex] || galleryImages[0] || "";
  const isListingStepComplete = Boolean(selectedProperty);

  useEffect(() => {
    if (!selectedProperty) {
      setIsGalleryOpen(false);
      setActiveGalleryIndex(0);
      setGalleryAnimationDirection("idle");
      setGalleryAnimationTick(0);
      return;
    }

    setActiveGalleryIndex((currentIndex) =>
      Math.min(currentIndex, Math.max(galleryImages.length - 1, 0))
    );
  }, [selectedProperty, galleryImages.length]);

  useEffect(() => {
    websiteBuildAttemptRef.current = null;
    setPreviewStage(PREVIEW_STAGE.idle);
    setPreviewModel(null);
    setPreviewError("");
    setPreviewBuildPhase(PREVIEW_BUILD_STEPS[0].key);
    setPersistWebsiteDraftError("");
  }, [selectedPropertyId]);

  useEffect(() => {
    if (previewStage === PREVIEW_STAGE.idle || !previewSectionRef.current) {
      return;
    }

    previewSectionRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [previewStage]);

  const setGalleryImage = (nextIndex, direction = "idle") => {
    setGalleryAnimationDirection(direction);
    setActiveGalleryIndex(nextIndex);
    setGalleryAnimationTick((currentTick) => currentTick + 1);
  };

  const resetPreviewState = () => {
    setPreviewStage(PREVIEW_STAGE.idle);
    setPreviewModel(null);
    setPreviewError("");
    setPreviewBuildPhase(PREVIEW_BUILD_STEPS[0].key);
    setPersistWebsiteDraftError("");
  };

  const ensureWebsiteBuildFlowStarted = ({ propertyId, templateKey }) => {
    const currentFlow = websiteBuildFlowRef.current;
    if (currentFlow && !currentFlow.hasTerminalEvent && !currentFlow.hasAbandonEvent) {
      return currentFlow;
    }

    const nextFlow = {
      flowId: createWebsiteBuildFlowId(),
      propertyId: String(propertyId || "").trim(),
      templateKey: String(templateKey || "").trim(),
      hasBuildStarted: false,
      hasTerminalEvent: false,
      hasAbandonEvent: false,
    };

    websiteBuildFlowRef.current = nextFlow;
    recordWebsiteHostAnalyticsEventSafely({
      propertyId: nextFlow.propertyId,
      eventType: WEBSITE_BUILD_FLOW_STARTED_EVENT,
      payload: {
        flowId: nextFlow.flowId,
        templateKey: nextFlow.templateKey,
      },
    });

    return nextFlow;
  };

  const markBuildFlowTerminal = (flowId = "") => {
    if (websiteBuildFlowRef.current?.flowId === flowId) {
      websiteBuildFlowRef.current.hasTerminalEvent = true;
    }
  };

  const recordBuildFlowAbandonmentIfNeeded = () => {
    const currentFlow = websiteBuildFlowRef.current;
    if (
      !currentFlow ||
      currentFlow.hasBuildStarted ||
      currentFlow.hasTerminalEvent ||
      currentFlow.hasAbandonEvent
    ) {
      return;
    }

    currentFlow.hasAbandonEvent = true;
    recordWebsiteHostAnalyticsEventSafely({
      propertyId: currentFlow.propertyId,
      eventType: WEBSITE_BUILD_FLOW_ABANDONED_EVENT,
      keepalive: true,
      payload: {
        flowId: currentFlow.flowId,
        templateKey: currentFlow.templateKey,
      },
    });
  };

  useEffect(() => {
    const normalizedPropertyId = String(selectedPropertyId || "").trim();
    if (!normalizedPropertyId) {
      return;
    }

    ensureWebsiteBuildFlowStarted({
      propertyId: normalizedPropertyId,
      templateKey: selectedTemplateId,
    });
  }, [selectedPropertyId, selectedTemplateId]);

  useEffect(() => {
    const handlePageHide = () => {
      recordBuildFlowAbandonmentIfNeeded();
    };

    globalThis.addEventListener("pagehide", handlePageHide);

    return () => {
      globalThis.removeEventListener("pagehide", handlePageHide);
      recordBuildFlowAbandonmentIfNeeded();
    };
  }, []);

  const startWebsiteBuildAttempt = () => {
    if (!selectedProperty) {
      return null;
    }

    const currentFlow = ensureWebsiteBuildFlowStarted({
      propertyId: selectedProperty.value,
      templateKey: selectedTemplateId,
    });
    currentFlow.hasBuildStarted = true;

    const nextAttempt = createWebsiteBuildAttempt({
      propertyId: selectedProperty.value,
      templateKey: selectedTemplateId,
      flowId: currentFlow.flowId,
    });

    websiteBuildAttemptRef.current = nextAttempt;
    recordWebsiteHostAnalyticsEventSafely({
      propertyId: nextAttempt.propertyId,
      eventType: WEBSITE_BUILD_STARTED_EVENT,
      payload: {
        attemptId: nextAttempt.attemptId,
        flowId: nextAttempt.flowId,
        templateKey: nextAttempt.templateKey,
      },
    });

    return nextAttempt;
  };

  const recordPreviewReadyForBuildAttempt = async (attempt) => {
    if (!attempt || attempt.hasPreviewReadyEvent) {
      return;
    }

    attempt.hasPreviewReadyEvent = true;
    const durationMs = getBuildAttemptDurationMs(attempt.startedAt);

    await recordWebsiteHostAnalyticsEventSafely({
      propertyId: attempt.propertyId,
      eventType: WEBSITE_PREVIEW_READY_EVENT,
      payload: {
        attemptId: attempt.attemptId,
        flowId: attempt.flowId,
        templateKey: attempt.templateKey,
        durationMs,
      },
    });
  };

  const recordBuildTerminalEvent = async ({ attempt, draftId = "", eventType, phase = "" }) => {
    if (!attempt || attempt.hasTerminalEvent) {
      return;
    }

    attempt.hasTerminalEvent = true;
    if (websiteBuildAttemptRef.current?.attemptId === attempt.attemptId) {
      websiteBuildAttemptRef.current = null;
    }
    markBuildFlowTerminal(attempt.flowId);
    if (eventType === WEBSITE_BUILD_SUCCEEDED_EVENT) {
      return;
    }

    try {
      await recordWebsiteHostAnalyticsEventWithRetry({
        propertyId: attempt.propertyId,
        draftId,
        eventType,
        payload: {
          attemptId: attempt.attemptId,
          flowId: attempt.flowId,
          templateKey: attempt.templateKey,
          durationMs: getBuildAttemptDurationMs(attempt.startedAt),
          phase,
        },
      });
    } catch {
      // Terminal failure analytics should not block the builder UX.
    }
  };

  const persistSelectedWebsiteDraft = async (buildAttempt = null) => {
    if (!selectedProperty) {
      return;
    }

    setIsPersistingWebsiteDraft(true);
    setPersistWebsiteDraftError("");

    try {
      const savedDraft = await upsertWebsiteDraft({
        propertyId: selectedProperty.value,
        templateKey: selectedTemplateId,
        status: "DRAFT",
        contentOverrides: {},
        themeOverrides: {},
        buildCompletion: buildAttempt
          ? {
              attemptId: buildAttempt.attemptId,
              flowId: buildAttempt.flowId,
              templateKey: buildAttempt.templateKey,
              durationMs: getBuildAttemptDurationMs(buildAttempt.startedAt),
              phase: WEBSITE_BUILD_FAILURE_PHASE_PERSIST,
            }
          : undefined,
      });
      await loadHostWebsiteDrafts();
      return savedDraft;
    } catch (error) {
      setPersistWebsiteDraftError(error?.message || "Preview is ready, but saving the website draft failed.");
      return null;
    } finally {
      setIsPersistingWebsiteDraft(false);
    }
  };

  const buildWebsitePreview = async () => {
    if (!selectedProperty || !selectedTemplateIsBuildable) {
      return;
    }

    const buildAttempt = startWebsiteBuildAttempt();
    setPreviewStage(PREVIEW_STAGE.loading);
    setPreviewModel(null);
    setPreviewError("");
    setPreviewBuildPhase(PREVIEW_BUILD_STEPS[0].key);

    try {
      const nextPreviewModel = await runWebsitePreviewBuildWorkflow({
        propertyId: selectedProperty.value,
        summaryProperty: selectedProperty,
        onPhaseChange: setPreviewBuildPhase,
      });

      setPreviewModel(nextPreviewModel);
      setPreviewStage(PREVIEW_STAGE.ready);
      await waitForNextPaint();
      await waitForNextPaint();
      await recordPreviewReadyForBuildAttempt(buildAttempt);
      const savedDraft = await persistSelectedWebsiteDraft(buildAttempt);
      if (!savedDraft) {
        await recordBuildTerminalEvent({
          attempt: buildAttempt,
          eventType: WEBSITE_BUILD_FAILED_EVENT,
          phase: WEBSITE_BUILD_FAILURE_PHASE_PERSIST,
        });
        return;
      }

      await recordBuildTerminalEvent({
        attempt: buildAttempt,
        draftId: savedDraft.id,
        eventType: WEBSITE_BUILD_SUCCEEDED_EVENT,
      });
      toast.success("Website built and ready for review.");
    } catch (error) {
      await recordBuildTerminalEvent({
        attempt: buildAttempt,
        eventType: WEBSITE_BUILD_FAILED_EVENT,
        phase: previewBuildPhase,
      });
      setPreviewStage(PREVIEW_STAGE.error);
      setPreviewModel(null);
      setPreviewError(error?.message || "We could not build the selected website preview.");
    }
  };

  useEffect(() => {
    if (!isGalleryOpen) {
      return undefined;
    }

    const documentBody = globalThis.document?.body;
    const previousOverflow = documentBody?.style.overflow ?? "";
    if (documentBody) {
      documentBody.style.overflow = "hidden";
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsGalleryOpen(false);
      }

      if (event.key === "ArrowLeft" && galleryImages.length > 1) {
        const nextIndex = (activeGalleryIndex - 1 + galleryImages.length) % galleryImages.length;
        setGalleryImage(nextIndex, "backward");
      }

      if (event.key === "ArrowRight" && galleryImages.length > 1) {
        const nextIndex = (activeGalleryIndex + 1) % galleryImages.length;
        setGalleryImage(nextIndex, "forward");
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);

    return () => {
      if (documentBody) {
        documentBody.style.overflow = previousOverflow;
      }
      globalThis.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeGalleryIndex, galleryImages.length, isGalleryOpen]);

  useEffect(() => {
    if (!websiteDraftPendingDelete) {
      return undefined;
    }

    const documentBody = globalThis.document?.body;
    const previousOverflow = documentBody?.style.overflow ?? "";
    if (documentBody) {
      documentBody.style.overflow = "hidden";
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeWebsiteDraftDeleteDialog();
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);

    return () => {
      if (documentBody) {
        documentBody.style.overflow = previousOverflow;
      }
      globalThis.removeEventListener("keydown", handleKeyDown);
    };
  }, [websiteDraftPendingDelete, isDeletingWebsiteDraft]);

  const openGallery = (imageIndex = 0) => {
    if (!selectedProperty || galleryImages.length < 1) {
      return;
    }

    setGalleryAnimationDirection("idle");
    setActiveGalleryIndex(Math.max(0, Math.min(imageIndex, galleryImages.length - 1)));
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setGalleryAnimationDirection("idle");
    setIsGalleryOpen(false);
  };

  const browseGallery = (direction) => {
    if (galleryImages.length < 2) {
      return;
    }

    const nextIndex = (activeGalleryIndex + direction + galleryImages.length) % galleryImages.length;
    setGalleryImage(nextIndex, direction < 0 ? "backward" : "forward");
  };

  const openWebsiteDraftEditor = (draft) => {
    const propertyId = String(draft?.propertyId || "").trim();
    if (!propertyId) {
      return;
    }

    navigate(`/hostdashboard/website/${propertyId}`);
  };

  const openWebsiteDraftLiveSite = async (draft) => {
    const propertyId = String(draft?.propertyId || "").trim();
    if (!propertyId) {
      return;
    }

    try {
      const siteSummary = await fetchWebsiteSiteByPropertyId(propertyId);
      const liveDomain = String(siteSummary?.primaryDomain?.domain || "").trim();
      const liveDomainStatus = String(siteSummary?.primaryDomain?.status || "").trim();
      const siteId = String(siteSummary?.site?.id || "").trim();
      const siteStatus = String(siteSummary?.site?.status || "").trim().toUpperCase();

      if (!liveDomain || siteStatus !== "PUBLISHED") {
        toast.error("Publish this website from the editor before opening the live site.");
        return;
      }

      globalThis.open(
        buildPublishedWebsiteHref(
          liveDomain,
          siteId,
          liveDomainStatus,
          siteSummary?.site?.siteName,
          siteSummary?.domains
        ),
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      toast.error(error?.message || "We could not open the live site for this website.");
      return;
    }
  };

  const openWebsiteDraftDeleteDialog = (draft) => {
    const propertyId = String(draft?.propertyId || "").trim();
    if (!propertyId) {
      return;
    }

    setWebsiteDraftPendingDelete(draft);
    setWebsiteDraftDeleteReasons([]);
    setWebsiteDraftDeleteStep(DELETE_WEBSITE_DRAFT_STEP_REASON);
  };

  const closeWebsiteDraftDeleteDialog = () => {
    if (isDeletingWebsiteDraft) {
      return;
    }

    setWebsiteDraftPendingDelete(null);
    setWebsiteDraftDeleteReasons([]);
    setWebsiteDraftDeleteStep(DELETE_WEBSITE_DRAFT_STEP_REASON);
  };

  const toggleWebsiteDraftDeleteReason = (reason) => {
    setWebsiteDraftDeleteReasons((currentReasons) =>
      currentReasons.includes(reason)
        ? currentReasons.filter((currentReason) => currentReason !== reason)
        : [...currentReasons, reason]
    );
  };

  const removeWebsiteDraft = async () => {
    const propertyId = getDraftPropertyId(websiteDraftPendingDelete);
    if (!propertyId) {
      setWebsiteDraftPendingDelete(null);
      return;
    }

    if (websiteDraftDeleteReasons.length < 1 || isDeletingWebsiteDraft) {
      return;
    }

    setIsDeletingWebsiteDraft(true);

    try {
      await deleteWebsiteDraft(propertyId, websiteDraftDeleteReasons);
      delete websiteDraftPreviewCacheKeysRef.current[propertyId];
      setWebsiteDraftPreviewModels((currentPreviewModels) => {
        const nextPreviewModels = { ...currentPreviewModels };
        delete nextPreviewModels[propertyId];
        return nextPreviewModels;
      });
      setWebsiteDraftLiveSiteStates((currentLiveSiteStates) => {
        const nextLiveSiteStates = { ...currentLiveSiteStates };
        delete nextLiveSiteStates[propertyId];
        return nextLiveSiteStates;
      });
      setWebsiteDraftPendingDelete(null);
      setWebsiteDraftDeleteReasons([]);
      setWebsiteDraftDeleteStep(DELETE_WEBSITE_DRAFT_STEP_REASON);
      await loadHostWebsiteDrafts();
      toast.success("Website deleted from your workspace.");
    } catch (error) {
      toast.error(error?.message || "We could not delete this website.");
    } finally {
      setIsDeletingWebsiteDraft(false);
    }
  };

  const renderPhotoStack = () => (
    <div className={styles.photoStack}>
      {previewImages.map((imageUrl, index) => (
        <button
          key={`${selectedProperty.value}-${index}-${imageUrl}`}
          type="button"
          className={getPhotoCardClassName(index)}
          onClick={() => openGallery(index)}
          aria-label={`Open photo ${index + 1} of ${selectedProperty.title || selectedProperty.label}`}
        >
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className={styles.photoImage}
          />
        </button>
      ))}
    </div>
  );

  const renderWorkspaceTabs = () => (
    <div className={styles.workspaceTabs} role="tablist" aria-label="Website workspace">
      <button
        type="button"
        role="tab"
        aria-selected={workspaceTab === WORKSPACE_TAB_WEBSITES}
        className={`${styles.workspaceTabButton} ${
          workspaceTab === WORKSPACE_TAB_WEBSITES ? styles.workspaceTabButtonActive : ""
        }`.trim()}
        onClick={() => setWorkspaceTab(WORKSPACE_TAB_WEBSITES)}
      >
        My websites
      </button>

      <button
        type="button"
        role="tab"
        aria-selected={workspaceTab === WORKSPACE_TAB_BUILDER}
        className={`${styles.workspaceTabButton} ${
          workspaceTab === WORKSPACE_TAB_BUILDER ? styles.workspaceTabButtonActive : ""
        }`.trim()}
        onClick={() => setWorkspaceTab(WORKSPACE_TAB_BUILDER)}
      >
        Build website
      </button>
    </div>
  );

  const renderWebsiteDraftOverview = () => {
    if (isLoadingWebsiteDrafts) {
      return (
        <div className={styles.stateCard}>
          <PulseBarsLoader message="Loading your saved website drafts..." />
        </div>
      );
    }

    if (websiteDraftsError) {
      return (
        <div className={`${styles.stateCard} ${styles.errorState}`}>
          <p>{websiteDraftsError}</p>
          <div className={styles.buttonRow}>
            <button type="button" className={styles.primaryButton} onClick={() => void loadHostWebsiteDrafts()}>
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (websiteDrafts.length < 1) {
      return (
        <div className={styles.stateCard}>
          <p>
            You do not have any saved website drafts yet. Build a website from the builder tab and the
            draft will appear here.
          </p>
        </div>
      );
    }

    return (
      <div className={styles.websiteDraftList}>
        {websiteDrafts.map((draft) => {
          const template = getWebsiteTemplateById(draft.templateKey);
          const templateName = template?.name || draft.templateKey || "Unknown template";
          const draftPreviewModel = websiteDraftPreviewModels[draft.propertyId] || null;
          const draftLiveSiteState = websiteDraftLiveSiteStates[draft.propertyId] || null;
          const draftPublishedAtLabel = formatPublishedAtLabel(draftLiveSiteState?.publishedAt);
          const draftDisplayTitle = getDraftDisplayTitle(draft, getDraftPublishedContentOverrides(draft));

          return (
            <article key={draft.id || `${draft.propertyId}-${draft.updatedAt}`} className={styles.websiteDraftCard}>
              <div className={styles.websiteDraftCardHeader}>
                <div className={styles.websiteDraftCardCopy}>
                  <p className={styles.summaryLabel}>Saved website draft</p>
                  <p className={styles.summaryValue}>
                    {draftDisplayTitle}
                  </p>
                  {draft.location ? <p className={styles.summaryLocation}>{draft.location}</p> : null}

                  <div className={styles.websiteDraftMetaRow}>
                    <span className={styles.metaText}>Template: {templateName}</span>
                    <span className={styles.metaText}>Updated: {formatDraftUpdatedAt(draft.updatedAt)}</span>
                  </div>

                  {draftLiveSiteState?.isStale ? (
                    <output className={styles.websiteDraftStaleNotice}>
                      <strong>
                        Your listing changed after the last publish
                        {draftPublishedAtLabel ? ` (${draftPublishedAtLabel})` : ""}.
                      </strong>{" "}
                      Guests still see the older version. Open the editor and use &ldquo;Update live site&rdquo;.
                    </output>
                  ) : null}

                  <div className={styles.buttonRow}>
                    <button
                      type="button"
                      className={styles.primaryButton}
                      onClick={() => openWebsiteDraftEditor(draft)}
                    >
                      Open editor
                    </button>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={() => void openWebsiteDraftLiveSite(draft)}
                    >
                      Open live site
                    </button>
                    <button
                      type="button"
                      className={`${styles.primaryButton} ${styles.dangerButton}`.trim()}
                      onClick={() => openWebsiteDraftDeleteDialog(draft)}
                    >
                      Delete permanently
                    </button>
                  </div>
                </div>

                <div className={styles.websiteDraftCardPreview}>
                  {draftPreviewModel ? (
                    <WebsiteTemplatePreview
                      templateId={draft.templateKey}
                      model={draftPreviewModel}
                      variant="compact"
                      viewport="desktop"
                    />
                  ) : (
                    <div className={styles.websiteDraftPreviewPlaceholder}>
                      <PulseBarsLoader message="Loading saved preview..." />
                    </div>
                  )}
                </div>

                <span className={styles.statusPill}>{draft.status || "DRAFT"}</span>
              </div>

              <WebsiteDomainPanel propertyId={String(draft.propertyId || "")} />
            </article>
          );
        })}
      </div>
    );
  };

  const renderSelectionState = () => {
    if (loadError && propertyOptions.length === 0) {
      return (
        <div className={`${styles.stateCard} ${styles.errorState}`}>
          <p>{loadError}</p>
          <div className={styles.buttonRow}>
            <button type="button" className={styles.primaryButton} onClick={() => void loadProperties()}>
              Try again
            </button>
          </div>
        </div>
      );
    }

    const showNoListingsState = isLoading === false && propertyOptions.length === 0;
    if (showNoListingsState) {
      return (
        <div className={styles.stateCard}>
          <p>
            You do not have any listings yet. Create one first, then come back here to start building
            your website.
          </p>
          <div className={styles.buttonRow}>
            <button type="button" className={styles.primaryButton} onClick={() => navigate("/hostonboarding")}>
              Add new accommodation
            </button>
          </div>
        </div>
      );
    }

    const showAllListingsUnavailableState =
      isLoading === false &&
      isLoadingWebsiteDrafts === false &&
      propertyOptions.length > 0 &&
      availablePropertyOptions.length === 0 &&
      !shouldKeepSelectedPropertyVisible;
    if (showAllListingsUnavailableState) {
      return (
        <div className={styles.stateCard}>
          <p>
            Every current listing already has a saved website draft or website attached to it. Delete an
            existing website first if you want that listing to become available again in the builder.
          </p>
          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setWorkspaceTab(WORKSPACE_TAB_WEBSITES)}
            >
              Open my websites
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="website-property-select">
            Listing
          </label>
          <select
            id="website-property-select"
            className={styles.selectInput}
            value={selectedPropertyId}
            onChange={(event) => setSelectedPropertyId(event.target.value)}
            disabled={isLoading || isLoadingWebsiteDrafts}
          >
            <option value={EMPTY_SELECTION}>
              {getPropertySelectPlaceholder({ isLoading, isLoadingWebsiteDrafts })}
            </option>
            {propertySelectOptions.map((propertyOption) => (
              <option key={propertyOption.value} value={propertyOption.value}>
                {propertyOption.label}
              </option>
            ))}
          </select>
        </div>

        {isLoadingWebsiteDrafts ? (
          <p className={styles.previewHelperText}>
            Checking which listings are already linked to a saved website.
          </p>
        ) : null}

        {selectedProperty ? (
          <div className={styles.selectionSummary}>
            <div className={styles.selectionPreview}>
              {renderPhotoStack()}

              <div className={styles.selectionContent}>
                <p className={styles.summaryLabel}>Selected listing</p>
                <p className={styles.summaryValue}>{selectedProperty.title || selectedProperty.label}</p>
                {selectedProperty.location ? (
                  <p className={styles.summaryLocation}>{selectedProperty.location}</p>
                ) : null}
                <div className={styles.metaRow}>
                  <span className={styles.statusPill}>
                    Status: {getPropertyStatusLabel(selectedProperty.status)}
                  </span>
                  <span className={styles.metaText}>{getImportedPhotoSummary(importedImageCount)}</span>
                </div>
                {summaryDescription ? <p className={styles.summaryMeta}>{summaryDescription}</p> : null}
                <div className={styles.buttonRow}>
                  <button type="button" className={styles.secondaryButton} onClick={() => openGallery(0)}>
                    Browse photos
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  };

  const renderTemplateStep = () => {
    const showTemplateAvailabilityHint = WEBSITE_TEMPLATE_OPTIONS.some(
      (templateOption) => isWebsiteTemplateBuilderEnabled(templateOption.id) === false
    );

    return (
      <div className={styles.templateStage}>
        <div className={styles.templateGrid}>
          {WEBSITE_TEMPLATE_OPTIONS.map(({ id }) => {
            const templateOption = getWebsiteTemplateById(id, language);
            const isSelected = templateOption.id === selectedTemplateId;
            const isBuilderEnabled = isWebsiteTemplateBuilderEnabled(templateOption.id);
            const isComingSoon = !isBuilderEnabled;

            return (
              <button
                key={templateOption.id}
                type="button"
                className={`${styles.templateCard} ${isSelected ? styles.templateCardSelected : ""} ${
                  isComingSoon ? styles.templateCardComingSoon : ""
                }`}
                onClick={() => {
                  if (isBuilderEnabled) {
                    setSelectedTemplateId(templateOption.id);
                  }
                }}
                disabled={isComingSoon}
                aria-pressed={isSelected}
              >
                <span className={styles.templateRadio} aria-hidden="true">
                  <span className={styles.templateRadioDot} />
                </span>
                {isSelected ? <span className={styles.templateSelectedTag}>{templateCopy.selected}</span> : null}
                {isComingSoon ? <span className={styles.templateComingSoonTag}>{templateCopy.comingSoon}</span> : null}
                <div className={styles.templatePreviewShell}>
                  <TemplateSilhouette layout={templateOption.layout} />
                </div>
                <div className={styles.templateCardContent}>
                  <div className={styles.templateCardHeader}>
                    <span className={styles.templateName}>{templateOption.name}</span>
                  </div>
                  <p className={styles.templateDescription}>{templateOption.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className={styles.templateSelectionState}>
          <div className={styles.templateSelectionHeader}>
            <div className={styles.templateSelectionCopy}>
              <p className={styles.summaryLabel}>{templateCopy.currentPick}</p>
              <p className={styles.selectedTemplateName}>{selectedTemplate.name}</p>
            </div>

            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => void buildWebsitePreview()}
              disabled={!selectedTemplateIsBuildable || previewStage === PREVIEW_STAGE.loading}
            >
              {previewStage === PREVIEW_STAGE.loading ? "Building preview..." : "Build my website"}
            </button>
          </div>

          <p className={styles.selectedTemplateDescription}>{selectedTemplate.description}</p>
          {showTemplateAvailabilityHint ? (
            <p className={styles.previewHelperText}>
              {templateCopy.availabilityHint}
            </p>
          ) : null}
        </div>
      </div>
    );
  };

  const renderPreviewBuildState = () => {
    const activeStepIndex = PREVIEW_BUILD_STEP_KEYS.indexOf(previewBuildPhase);

    return (
      <div className={styles.previewBuildCard}>
        <div className={styles.previewBuildVisual}>
          <div className={`${styles.previewBuildSilhouetteShell} ${styles.templatePreviewShell}`}>
            <TemplateSilhouette layout={selectedTemplate.layout} />
          </div>
        </div>

        <div className={styles.previewBuildCopy}>
          <PulseBarsLoader
            message={`Preparing ${selectedTemplate.name} from the selected listing`}
            className={styles.previewLoader}
          />

          <div className={styles.previewBuildSteps}>
            {PREVIEW_BUILD_STEPS.map((step, index) => {
              const isComplete = index < activeStepIndex;
              const isActive = index === activeStepIndex;

              return (
                <article
                  key={step.key}
                  className={`${styles.previewBuildStep} ${isComplete ? styles.previewBuildStepComplete : ""} ${
                    isActive ? styles.previewBuildStepActive : ""
                  }`.trim()}
                >
                  <span className={styles.previewBuildStepIndex} aria-hidden="true">
                    {index + 1}
                  </span>
                  <div className={styles.previewBuildStepCopy}>
                    <p>{step.title}</p>
                    <span>{step.description}</span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewStep = () => {
    if (previewStage === PREVIEW_STAGE.idle) {
      return null;
    }

    return (
      <section ref={previewSectionRef} className={styles.builderStepSection}>
        <div className={styles.stepHeader}>
          <p className={styles.stepEyebrow}>Step 3</p>
          <h2>Preview your website</h2>
          <p>
            This stage uses the selected listing detail response and maps it into a shared content model
            before rendering the chosen template.
          </p>
        </div>

        {previewStage === PREVIEW_STAGE.loading ? renderPreviewBuildState() : null}

        {previewStage === PREVIEW_STAGE.error ? (
          <div className={`${styles.stateCard} ${styles.errorState}`}>
            <p>{previewError}</p>
            <div className={styles.buttonRow}>
              <button type="button" className={styles.primaryButton} onClick={() => void buildWebsitePreview()}>
                Try preview again
              </button>
              <button type="button" className={styles.secondaryButton} onClick={resetPreviewState}>
                Back to template chooser
              </button>
            </div>
          </div>
        ) : null}

        {previewStage === PREVIEW_STAGE.ready && previewModel ? (
          <div className={styles.previewReadyState}>
            <div className={styles.previewStageActions}>
              <div className={styles.previewStageMessageStack}>
                <span className={styles.previewHelperText}>
                  Continue into the editor to refine this Panorama website with the imported listing data.
                </span>
                {isPersistingWebsiteDraft ? (
                  <span className={styles.previewHelperText}>Saving website draft to your workspace...</span>
                ) : null}
                {persistWebsiteDraftError ? (
                  <span className={styles.previewPersistErrorText}>{persistWebsiteDraftError}</span>
                ) : null}
              </div>
              <div className={styles.buttonRow}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => navigate(`/hostdashboard/website/${selectedProperty.value}`)}
                  disabled={isPersistingWebsiteDraft || Boolean(persistWebsiteDraftError)}
                >
                  Open editor
                </button>
                <button type="button" className={styles.primaryButton} onClick={() => void buildWebsitePreview()}>
                  Refresh preview
                </button>
              </div>
            </div>

            <WebsiteTemplatePreview templateId={selectedTemplateId} model={previewModel} />
          </div>
        ) : null}
      </section>
    );
  };

  return (
    <main className="page-Host">
      <div className="page-Host-content">
        <section className={styles.websitePage}>
          <div className={styles.heroCard}>
            <p className={styles.eyebrow}>Standalone property website</p>
            <h1 className={styles.heroTitle}>Build your own free website for one of your listings</h1>
            <p className={styles.heroDescription}>
              Choose one of your Domits listings to start a direct booking website. We use the property
              information you already manage in Domits, so the website setup begins from real listing data
              instead of manual re-entry.
            </p>
          </div>

          <div className={styles.selectorCard}>
            <div className={styles.selectorHeader}>
              <div className={styles.stepTitleRow}>
                <span className={styles.titleIconBadge} aria-hidden="true">
                  <LanguageIcon className={styles.titleIcon} />
                </span>
                <h2>Configure your website</h2>
              </div>
              <p>
                Your website starts from the property data you already manage in Domits. Title,
                description, photos, and other core listing details are imported directly into the new
                site, so you can assemble your site without any technical knowledge.
              </p>
            </div>

            {renderWorkspaceTabs()}

            {workspaceTab === WORKSPACE_TAB_BUILDER ? (
              <div className={styles.builderSteps}>
                <section className={styles.builderStepSection}>
                  <div className={styles.stepHeader}>
                    <p className={styles.stepEyebrow}>Step 1</p>
                    <div className={styles.stepTitleRow}>
                      <span className={styles.titleIconBadge} aria-hidden="true">
                        <HomeIcon className={styles.titleIcon} />
                      </span>
                      <h2>Choose your listing</h2>
                    </div>
                    <p>
                      Pick the property you want to use as the base for your website. This page only
                      shows listings that belong to your host account.
                    </p>
                  </div>

                  {renderSelectionState()}
                </section>

                {isListingStepComplete ? (
                  <>
                    <section className={styles.builderStepSection}>
                      <div className={styles.stepHeader}>
                        <p className={styles.stepEyebrow}>Step 2</p>
                        <h2>{templateCopy.title}</h2>
                        <p>
                          Select the layout direction you want to use for the listing website. You can keep
                          adjusting the listing choice above while you compare template options.
                        </p>
                      </div>

                      {renderTemplateStep()}
                    </section>

                    {renderPreviewStep()}
                  </>
                ) : null}
              </div>
            ) : (
              <section className={styles.builderStepSection}>
                <div className={styles.stepHeader}>
                  <p className={styles.stepEyebrow}>Website workspace</p>
                  <h2>Saved websites</h2>
                  <p>
                    These are your persisted website drafts. Open any draft to move into the dedicated
                    editor page and continue adjusting your website's content there.
                  </p>
                </div>

                {renderWebsiteDraftOverview()}
              </section>
            )}
          </div>
        </section>
      </div>

      {isGalleryOpen && selectedProperty ? (
        <dialog
          open
          className="image-overlay"
          aria-label="Listing gallery"
          onCancel={(event) => {
            event.preventDefault();
            closeGallery();
          }}
        >
          <button
            type="button"
            className="close-overlay-button"
            onClick={closeGallery}
            aria-label="Close image gallery"
          >
            X
          </button>

          <div className="overlay-center-wrapper">
            <button
              type="button"
              className="nav-button left"
              onClick={() => browseGallery(-1)}
              disabled={galleryImages.length < 2}
              aria-label="Show previous image"
            >
              <img src={arrowLeftIcon} alt="" aria-hidden="true" className={styles.galleryNavIcon} />
            </button>

            <div className={styles.galleryImageViewport}>
              <img
                key={`gallery-image-${activeGalleryIndex}-${galleryAnimationTick}`}
                className={`overlay-main-image ${styles.galleryAnimatedImage} ${getGalleryAnimationClassName(
                  galleryAnimationDirection
                )}`.trim()}
                src={activeGalleryImage}
                alt={getGalleryViewAlt(selectedProperty.title || selectedProperty.label, activeGalleryIndex)}
              />
            </div>

            <button
              type="button"
              className="nav-button right"
              onClick={() => browseGallery(1)}
              disabled={galleryImages.length < 2}
              aria-label="Show next image"
            >
              <img src={arrowRightIcon} alt="" aria-hidden="true" className={styles.galleryNavIcon} />
            </button>
          </div>

          <div className="overlay-thumbnails">
            {galleryImages.map((imageUrl, index) => (
              <button
                key={`${selectedProperty.value}-gallery-${index}-${imageUrl}`}
                type="button"
                className={`${index === activeGalleryIndex ? "thumb active" : "thumb"} ${styles.galleryThumbnailButton}`}
                onClick={() => {
                  if (index === activeGalleryIndex) {
                    return;
                  }

                  setGalleryImage(index, index > activeGalleryIndex ? "forward" : "backward");
                }}
                aria-label={`Show gallery item ${index + 1} for ${selectedProperty.title || selectedProperty.label}`}
              >
                <img
                  src={imageUrl}
                  alt={`Gallery item ${index + 1} for ${selectedProperty.title || selectedProperty.label}`}
                  className={styles.galleryThumbnailImage}
                />
              </button>
            ))}
          </div>
        </dialog>
      ) : null}

      <WebsiteDraftDeleteDialog
        draft={websiteDraftPendingDelete}
        deleteReasons={websiteDraftDeleteReasons}
        deleteStep={websiteDraftDeleteStep}
        isDeleting={isDeletingWebsiteDraft}
        onClose={closeWebsiteDraftDeleteDialog}
        onBackToReasons={() => setWebsiteDraftDeleteStep(DELETE_WEBSITE_DRAFT_STEP_REASON)}
        onConfirm={() => void removeWebsiteDraft()}
        onReasonToggle={toggleWebsiteDraftDeleteReason}
        onReasonNext={() => setWebsiteDraftDeleteStep(DELETE_WEBSITE_DRAFT_STEP_CONFIRM)}
      />
    </main>
  );
}

export default WebsiteBuilderPage;
